/**
 * The worker chip: their mark, their name, and their craft, on a row or in a
 * pane header.
 *
 * The craft rides beside the name by default ("Wright · Builder"), because a
 * name alone answers "who" and leaves "which one is the product manager"
 * unanswered on every screen that names more than one worker. A row whose
 * own sentence already says what they did may pass `craft={false}` and print
 * the name alone. With a verb the chip reads "Name · Craft · verb" — same
 * order, the act last.
 *
 * The craft gets its own span in the fact voice rather than sharing the name's
 * weight, so the NAME stays the anchor a reader scans for and the craft reads
 * as the annotation it is. Same treatment as `.twr-craft` on the Team roster,
 * because one worker should look like one worker on every surface.
 *
 * Renders NOTHING without a worker. An item the Team's own spine raised has no
 * `persona_slug`, and a blank mark next to the words "no worker" would be a
 * placeholder for a fact that is already complete: nobody in particular did
 * this. Silence is the render.
 *
 * `WorkerMark` is the Workers surface's own atom and is reused verbatim so a
 * worker looks identical wherever they appear.
 */
import type { WorkerSummary } from "@yagni/shared";
import { WorkerMark } from "../workers/WorkerMark.js";

export function WorkerChip({
  worker,
  /** Appended after a middot, e.g. "proposed" → "Bailey · proposed". */
  verb,
  /** Print the craft after the name. Default true; a row whose sentence already names the act passes false. */
  craft = true,
  testId,
}: {
  worker: WorkerSummary | null;
  verb?: string;
  craft?: boolean;
  testId?: string;
}) {
  if (!worker) return null;
  return (
    <span className="wk-worker-chip" data-testid={testId ?? `worker-chip-${worker.slug}`}>
      <WorkerMark
        avatar={worker.avatar}
        name={worker.displayName}
        slug={worker.slug}
        size="sm"
      />
      <span className="wk-worker-chip-name">{worker.displayName}</span>
      {craft && worker.craft ? (
        <>
          {" · "}
          <span className="wk-worker-chip-craft">{worker.craft}</span>
        </>
      ) : null}
      {verb ? (
        <>
          {" · "}
          <span className="wk-worker-chip-verb">{verb}</span>
        </>
      ) : null}
    </span>
  );
}
